import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import PublicLayout from '../components/PublicLayout';
import { API_BASE_URL } from '../utils/apiBase';

const fallbackApkUrl = API_BASE_URL
  ? `${API_BASE_URL.replace(/\/+$/, '')}/users/mobile-release/apk`
  : '/downloads/shimbirr-mobile-latest.apk';
const fallbackManifestUrl = '/downloads/shimbirr-mobile-release-manifest.json';
const tabanPortalUrl =
  process.env.REACT_APP_TABAN_PORTAL_URL || 'https://taban.so';

const baseHighlights = [
  'Install the latest Android build from the web and reach the same release inside the in-app updater.',
  'Use verified phone identity, premium profile controls, and connected-product management from one place.',
  'See online friends, last-seen status, and recent chats first on the refreshed Shimbirr friends experience.',
  'Keep requests, QR discovery, add-by-code, and contact matching in a separate People & tools page.',
  'Keep one Shimbirr profile for Taban access, suite approvals, software OTPs, and notification control.',
];

const installSteps = [
  'Download the Android APK to your phone from this page.',
  'Open the APK from Chrome, Files, or Downloads and allow installs from this source if Android asks.',
  'Tap Install, open Shimbirr, and sign in. Future updates can still come from the in-app updater.',
];

function normalizeArtifact(item, websiteBase = '') {
  const fileName = `${item?.fileName || ''}`.trim();
  const rawUrl = `${item?.url || ''}`.trim();
  const sizeCandidate = item?.sizeBytes ?? item?.bytes;
  const parsedSize = Number(sizeCandidate);
  const sizeBytes =
    Number.isFinite(parsedSize) && parsedSize > 0 ? parsedSize : null;
  const safeBase = `${websiteBase || ''}`.replace(/\/+$/, '');
  const derivedUrl =
    rawUrl ||
    (fileName
      ? `${safeBase}/downloads/${encodeURIComponent(fileName)}`
      : '');

  return {
    id: item?.id || fileName || rawUrl,
    kind: item?.kind || (fileName.toLowerCase().endsWith('.aab') ? 'aab' : 'apk'),
    abi: item?.abi || null,
    label: item?.label || fileName || 'Download',
    fileName,
    sizeBytes,
    publishedAt: item?.publishedAt || null,
    sha256: `${item?.sha256 || ''}`.trim() || null,
    installable: item?.installable !== false,
    url: derivedUrl,
  };
}

function releaseFromManifest(manifest) {
  const websiteBase =
    typeof window !== 'undefined' ? window.location.origin : '';
  const artifacts = (Array.isArray(manifest?.artifacts) ? manifest.artifacts : [])
    .map((item) => normalizeArtifact(item, websiteBase))
    .filter((item) => item.url.trim().length > 0);
  const installArtifact =
    artifacts.find((item) => item.installable !== false) || null;

  return {
    version: 'Latest build',
    versionName: 'Latest build',
    buildNumber: null,
    apkUrl: installArtifact?.url || fallbackApkUrl,
    apkSha256: installArtifact?.sha256 || null,
    headline:
      'Download the latest Shimbirr Android build directly from the published release package.',
    publishedAt:
      installArtifact?.publishedAt || manifest?.generatedAt || null,
    sizeBytes: installArtifact?.sizeBytes || null,
    artifacts,
    releaseNotes: [
      'The live release feed was unavailable, so this page switched to the latest published download manifest.',
      'The install package below is still the newest public Android build for direct download.',
      'If you already have Shimbirr installed, you can also update later from inside the app.',
    ],
    usingManifestFallback: true,
  };
}

function formatBytes(bytes) {
  const value = Number(bytes || 0);
  if (!Number.isFinite(value) || value <= 0) return 'Size updates after publish';
  const units = ['B', 'KB', 'MB', 'GB'];
  let size = value;
  let index = 0;
  while (size >= 1024 && index < units.length - 1) {
    size /= 1024;
    index += 1;
  }
  return `${size.toFixed(size >= 100 || index === 0 ? 0 : 1)} ${units[index]}`;
}

function formatPublishedAt(value) {
  if (!value) return 'Fresh build';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Fresh build';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export default function Download() {
  const [release, setRelease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadManifestFallback(message = '') {
      try {
        const response = await fetch(fallbackManifestUrl, { cache: 'no-store' });
        if (!response.ok) {
          throw new Error('Could not load the published download manifest.');
        }
        const data = await response.json();
        if (cancelled) return false;
        setRelease(releaseFromManifest(data));
        setError(
          message
            ? `${message} Showing the latest published Android package instead.`
            : '',
        );
        setLoading(false);
        return true;
      } catch (manifestError) {
        if (cancelled) return false;
        setError(
          message ||
            manifestError.message ||
            'Could not load the latest mobile release.',
        );
        setLoading(false);
        return false;
      }
    }

    async function loadRelease() {
      if (!API_BASE_URL) {
        await loadManifestFallback();
        return;
      }

      try {
        const response = await fetch(
          `${API_BASE_URL.replace(/\/+$/, '')}/users/mobile-release`,
        );
        if (!response.ok) {
          throw new Error('Could not load the latest mobile release.');
        }
        const data = await response.json();
        if (cancelled) return;
        setRelease(data);
        setError('');
        setLoading(false);
      } catch (loadError) {
        if (cancelled) return;
        await loadManifestFallback(
          loadError.message || 'Could not load the latest mobile release.',
        );
      }
    }

    void loadRelease();
    return () => {
      cancelled = true;
    };
  }, []);

  const artifacts = useMemo(() => {
    const items = Array.isArray(release?.artifacts) ? release.artifacts : [];
    const websiteBase =
      typeof window !== 'undefined' ? window.location.origin : '';
    return items
      .map((item) => normalizeArtifact(item, websiteBase))
      .filter((item) => item.url.trim().length > 0);
  }, [release]);
  const installArtifact =
    artifacts.find((item) => item.installable !== false) || null;
  const apkUrl = installArtifact?.url || release?.apkUrl || fallbackApkUrl;
  const apkFileName = installArtifact?.fileName || 'shimbirr-mobile-latest.apk';
  const versionLabel = release?.versionName || release?.version || 'Latest build';
  const buildNumber = release?.buildNumber ? `Build ${release.buildNumber}` : '';
  const publishedLabel = formatPublishedAt(release?.publishedAt);
  const sizeLabel = formatBytes(release?.sizeBytes);
  const checksum = release?.apkSha256 || installArtifact?.sha256 || '';
  const headline =
    release?.headline ||
    'Download the newest Shimbirr Android build for verified identity, social alerts, and connected-suite access.';
  const notes = useMemo(() => {
    const items = Array.isArray(release?.releaseNotes) ? release.releaseNotes : [];
    return items.filter((item) => `${item}`.trim().length > 0);
  }, [release]);

  return (
    <PublicLayout>
      <main className="bg-[#071116] px-6 py-10 text-white sm:py-16">
        <div className="mx-auto max-w-6xl">
          <div className="rounded-[2.5rem] border border-white/10 bg-[radial-gradient(circle_at_top,rgba(245,178,33,0.16),transparent_28%),linear-gradient(180deg,rgba(255,255,255,0.05),rgba(255,255,255,0.03))] p-8 shadow-[0_30px_90px_rgba(0,0,0,0.28)] backdrop-blur-xl lg:p-10">
            <p className="text-xs font-semibold uppercase tracking-[0.32em] text-amber-200">
              Shimbirr Download
            </p>
            <h1 className="mt-4 max-w-4xl text-4xl font-black tracking-[-0.04em] text-white sm:text-5xl">
              Download the current Shimbirr mobile release from the web.
            </h1>
            <p className="mt-5 max-w-4xl text-lg leading-8 text-slate-300">
              {headline}
            </p>

            <div className="mt-8 flex flex-wrap gap-4">
              <a
                href={apkUrl}
                download={apkFileName}
                className="rounded-full bg-gradient-to-r from-amber-300 via-yellow-400 to-amber-300 px-7 py-3 font-semibold text-slate-950 shadow-[0_18px_50px_rgba(245,178,33,0.28)] transition hover:-translate-y-0.5"
              >
                Download Android APK
              </a>
              <a
                href={tabanPortalUrl}
                target="_blank"
                rel="noreferrer"
                className="rounded-full border border-amber-300/30 bg-amber-300/10 px-7 py-3 font-semibold text-amber-100 transition hover:bg-amber-300/15"
              >
                Open Taban Portal
              </a>
              <Link
                to="/"
                className="rounded-full border border-white/15 px-7 py-3 font-semibold text-white transition hover:border-white/30 hover:bg-white/5"
              >
                Back to Landing
              </Link>
            </div>

            <div className="mt-10 grid gap-4 lg:grid-cols-4">
              {baseHighlights.map((item) => (
                <div
                  key={item}
                  className="rounded-[1.5rem] border border-white/10 bg-black/20 p-5"
                >
                  <p className="text-sm leading-7 text-slate-200">{item}</p>
                </div>
              ))}
            </div>

            <div className="mt-8 grid gap-4 lg:grid-cols-[0.85fr_1.15fr]">
              <div className="rounded-[1.75rem] border border-emerald-400/20 bg-emerald-400/10 p-5">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-emerald-200">
                  Current release
                </p>
                <p className="mt-3 text-3xl font-black tracking-[-0.04em] text-white">
                  {versionLabel}
                </p>
                <p className="mt-2 text-sm leading-7 text-emerald-50/90">
                  {buildNumber ? `${buildNumber} · ` : ''}
                  {sizeLabel} · Published {publishedLabel}
                </p>
                <p className="mt-4 text-sm leading-7 text-emerald-50/90">
                  This is the same release feed used by the in-app updater inside
                  Shimbirr, so users can fetch the current APK from either place.
                </p>
                <div className="mt-5 rounded-[1.1rem] border border-white/10 bg-black/20 p-4">
                  <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-100/80">
                    Install on Android
                  </p>
                  <div className="mt-3 space-y-3">
                    {installSteps.map((step, index) => (
                      <div
                        key={step}
                        className="flex items-start gap-3 rounded-[1rem] border border-white/8 bg-white/[0.03] p-3"
                      >
                        <span className="mt-0.5 inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-200/15 text-xs font-bold text-emerald-100">
                          {index + 1}
                        </span>
                        <p className="text-sm leading-6 text-emerald-50/90">{step}</p>
                      </div>
                    ))}
                  </div>
                </div>
                {loading ? (
                  <p className="mt-4 text-sm text-amber-100">Checking latest release…</p>
                ) : null}
                {error ? (
                  <p className="mt-4 text-sm text-amber-100">
                    {error} The direct APK link above still works with the latest published file.
                  </p>
                ) : null}
                {checksum ? (
                  <div className="mt-4 rounded-[1rem] border border-white/10 bg-black/20 p-4">
                    <p className="text-xs font-semibold uppercase tracking-[0.22em] text-emerald-100/80">
                      APK checksum
                    </p>
                    <p className="mt-2 break-all font-mono text-xs leading-6 text-emerald-50/85">
                      {checksum}
                    </p>
                  </div>
                ) : null}
                {artifacts.length > 1 ? (
                  <div className="mt-5 space-y-3">
                    {artifacts.map((artifact) => (
                      <a
                        key={artifact.id || artifact.url}
                        href={artifact.url}
                        download={artifact.fileName || undefined}
                        className="block rounded-[1.1rem] border border-white/10 bg-black/20 p-4 transition hover:border-emerald-200/30 hover:bg-black/30"
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div>
                            <p className="text-sm font-semibold text-white">
                              {artifact.label || 'Download'}
                            </p>
                            <p className="mt-1 text-xs uppercase tracking-[0.18em] text-emerald-100/80">
                              {artifact.installable === false ? 'Store package' : artifact.abi ? `Smaller install · ${artifact.abi}` : 'Works on most Android devices'}
                            </p>
                          </div>
                          <span className="text-sm font-semibold text-emerald-100">
                            {formatBytes(artifact.sizeBytes)}
                          </span>
                        </div>
                      </a>
                    ))}
                  </div>
                ) : null}
              </div>

              <div className="rounded-[1.75rem] border border-white/10 bg-black/20 p-5">
                <p className="text-sm font-semibold uppercase tracking-[0.24em] text-amber-200">
                  Included now
                </p>
                {release?.usingManifestFallback ? (
                  <p className="mt-4 text-sm leading-7 text-slate-300">
                    Live release details were unavailable, so this page is using the
                    published download manifest and direct file links to keep installs
                    available.
                  </p>
                ) : null}
                <div className="mt-4 space-y-3">
                  {(notes.length ? notes : baseHighlights).map((item) => (
                    <div
                      key={item}
                      className="rounded-[1.1rem] border border-white/8 bg-white/[0.03] p-4"
                    >
                      <p className="text-sm leading-7 text-slate-200">{item}</p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </PublicLayout>
  );
}
